import type { ISeoPage } from '@dev-fast/types';
import {
  filterSeoRoutesByDisabledServicePrefixes,
  getDisabledServiceSeoRoutePrefixes,
} from '@dev-fast/types/constants/navigation-seo-routes.constants';

import {
  type AppSurfaceLobbySeoPagesKey,
  type AppSurfaceSeoRoutesKey,
  type Environments,
  getEnvironmentGameRouteKey,
  getEnvironmentLobbySeoPagesKey,
  getEnvironmentSeoRoutesKey,
  isEnvironmentGameEnabled,
  isEnvironmentNavigationItemEnabled,
} from '@app/core/environment-service';

import { LOBBY_SEO_PAGES } from '../constants/lobby-seo-pages.constant';
import { LOBBY_SEO_PAGES as LOBBY_SEO_PAGES_CASES } from '../constants/lobby-seo-pages.constant.cases';
import { LOBBY_SEO_PAGES as LOBBY_SEO_PAGES_FASTCS2 } from '../constants/lobby-seo-pages.constant.fastcs2';
import { seoRoutes as seoRoutesCases } from '../constants/seo-routes.const.cases';
import { seoRoutes as seoRoutesCasesModes } from '../constants/seo-routes.const.cases-modes';
import { seoRoutes as seoRoutesFastcs2 } from '../constants/seo-routes.const.fastcs2';
import { seoRoutes } from '../constants/seo-routes.const.prod';
import { seoRoutesStore } from '../constants/seo-routes.const.store-prod';

const SEO_ROUTES_BY_KEY: Record<AppSurfaceSeoRoutesKey, string[]> = {
  default: seoRoutes,
  fastcs2: seoRoutesFastcs2,
  'cases-modes': seoRoutesCasesModes,
  cases: seoRoutesCases,
  store: seoRoutesStore,
};

const LOBBY_SEO_PAGES_BY_KEY: Record<AppSurfaceLobbySeoPagesKey, ISeoPage[]> = {
  default: LOBBY_SEO_PAGES,
  cases: LOBBY_SEO_PAGES_CASES,
  fastcs2: LOBBY_SEO_PAGES_FASTCS2,
};

const filterSeoRoutesByEnabledGames = (environment: Environments, sourceRoutes: readonly string[]): string[] => {
  const enabledGameRoutes = (environment.MODULES.games ?? [])
    .filter((game) => isEnvironmentGameEnabled(environment, game))
    .map((game) => `/game/${getEnvironmentGameRouteKey(environment, game)}`);

  return sourceRoutes.filter((route) => {
    if (!route.startsWith('/game/')) {
      return true;
    }

    return enabledGameRoutes.some((gameRoute) => route === gameRoute || route.startsWith(`${gameRoute}/`));
  });
};

export const getAppSeoRoutes = (environment: Environments): string[] => {
  const routes = filterSeoRoutesByEnabledGames(environment, SEO_ROUTES_BY_KEY[getEnvironmentSeoRoutesKey(environment)]);
  // Services disabled on this surface (e.g. betting on the EU build) take their non-/game pages
  // with them; the prerender/sitemap scripts apply the same map on their side.
  const disabledPrefixes = getDisabledServiceSeoRoutePrefixes((item) => isEnvironmentNavigationItemEnabled(environment, item));

  return filterSeoRoutesByDisabledServicePrefixes(routes, disabledPrefixes);
};

export const getAppLobbySeoPages = (environment: Environments): ISeoPage[] => {
  return LOBBY_SEO_PAGES_BY_KEY[getEnvironmentLobbySeoPagesKey(environment)];
};
